import React, { useState } from "react";
import { useWindows } from "@app/overlay";
import { FormFooter } from "@shared/ui/form-footer";
import { MobileWindowFrame } from "@shared/ui/overlay/mobile-window-frame/MobileWindowFrame";
import { useWindowControls } from "@shared/ui/overlay/hooks/useWindowControls";
import { PickerField } from "@shared/ui/picker-field";
import type { BusinessPickerFieldProps, BusinessPickerFieldRenderWindowContentProps } from "../types";
import "./BusinessPickerField.scss";

const defaultSummaryFormat = (count: number): string => (count === 0 ? "Не выбрано" : `Выбрано: ${count}`);

/**
 * Пропсы внутреннего стандартного окна {@link BusinessPickerField}.
 * @template Value тип значения внутри `value[]`
 */
interface BusinessPickerFieldContentWindowProps<Value> {
    /** Заголовок окна. */
    title: string;
    /** Начальное draft-значение. */
    value: Value[];
    /** Блокировка интерактива внутри контента. */
    readonly: boolean;
    /** Показывать confirm/cancel footer. */
    showConfirmFooter: boolean;
    /** className {@link MobileWindowFrame}. */
    className?: string;
    /**
     * Рендер контента окна.
     * @param props runtime-контекст (`value`, `onChange`, `readonly`, `close`, `submit`)
     */
    renderContent: (props: BusinessPickerFieldRenderWindowContentProps<Value>) => React.ReactNode;
    /**
     * Подтверждает результат наружу.
     * @param values итоговый список
     */
    onSubmit?: (values: Value[]) => void;
}

/**
 * Формирует runtime-опции открытия overlay-окна для {@link BusinessPickerField}.
 * @param window UI/runtime-настройки из `props.window`
 * @param windowKey явный логический key из `props.windowKey`
 */
function getOpenWindowOptions(window: BusinessPickerFieldProps["window"], windowKey?: string) {
    const key = windowKey ?? window?.key;
    const reuse = window?.reuse ?? (key == null ? undefined : "replace");
    const { className: _className, ...openOptions } = window ?? {};

    return {
        blocking: true,
        placement: "fullscreen" as const,
        ...openOptions,
        key,
        reuse,
    };
}

/**
 * Стандартная window-реализация для `renderWindowContent`.
 * @template Value тип значения внутри `value[]`
 */
function BusinessPickerFieldContentWindow<Value>({
    title,
    value,
    readonly,
    showConfirmFooter,
    className,
    renderContent,
    onSubmit,
}: BusinessPickerFieldContentWindowProps<Value>): React.ReactElement {
    const windowControls = useWindowControls();
    const [localValues, setLocalValues] = useState<Value[]>(value);

    const close = () => {
        windowControls.close();
    };

    const submit = (next?: Value[]) => {
        onSubmit?.(next ?? localValues);
        windowControls.close();
    };

    const footer = showConfirmFooter ? (
        <FormFooter
            onConfirm={() => {
                submit();
            }}
            onCancel={close}
        />
    ) : null;

    return (
        <MobileWindowFrame title={title} className={className} onRequestClose={close} footer={footer}>
            <div className="business_picker_field__window">
                {renderContent({
                    value: localValues,
                    onChange: setLocalValues,
                    readonly,
                    close,
                    submit,
                })}
            </div>
        </MobileWindowFrame>
    );
}

/**
 * Business-адаптер picker-поля с подтверждением значения в app overlay-window.
 *
 * Снаружи — {@link PickerField} со сводкой; по клику открывается fullscreen window. \
 * Для простых сценариев — `renderWindowContent` (draft + optional FormFooter). \
 * Для сложных доменных редакторов — `windowDefinition`.
 *
 * Не использует AdaptivePickerOverlay: владение поверхностью — у app window stack. \
 * Chrome trigger совпадает с Select/SearchSelect через `@omega/ui` PickerField.
 *
 * @template Value тип значения внутри `value[]`
 * @see StorePlaceCellPickerField — пример доменной обёртки
 */
export function BusinessPickerField<Value = number>(props: BusinessPickerFieldProps<Value>): React.ReactElement {
    const {
        value,
        onChange,
        windowTitle,
        label,
        required,
        error,
        disabled = false,
        placeholder,
        summaryFormat = defaultSummaryFormat,
        renderWindowContent,
        showConfirmFooter = true,
        windowDefinition,
        windowKey,
        window,
    } = props;

    const windows = useWindows();
    const values = value ?? [];
    const summary = summaryFormat(values.length);
    const displayValue = summary.length > 0 ? summary : undefined;
    const openWindowOptions = getOpenWindowOptions(window, windowKey);
    const canClear = values.length > 0 && onChange != null && !disabled;

    const handleOpenWindow = () => {
        if (disabled) {
            return;
        }

        if (windowDefinition != null) {
            windowDefinition.open(
                windows,
                {
                    title: windowTitle,
                    value: values.length > 0 ? [...values] : [],
                    readonly: disabled,
                    onSubmit: (next) => {
                        onChange?.(next);
                    },
                },
                openWindowOptions,
            );
            return;
        }

        if (renderWindowContent == null) {
            return;
        }

        windows.open(
            BusinessPickerFieldContentWindow<Value>,
            {
                title: windowTitle,
                value: values.length > 0 ? [...values] : [],
                readonly: disabled,
                showConfirmFooter,
                className: window?.className ?? "business_picker_field__frame",
                renderContent: renderWindowContent,
                onSubmit: onChange,
            },
            openWindowOptions,
        );
    };

    const handleClear = () => {
        onChange?.([]);
    };

    return (
        <PickerField
            className="business_picker_field"
            label={label}
            required={required}
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            displayValue={displayValue}
            onOpen={handleOpenWindow}
            onClear={canClear ? handleClear : undefined}
        />
    );
}
